import { useCallback, useState } from 'react';
import { injectIntl } from 'react-intl';
import { List, Checkbox } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import classnames from 'classnames';

import { getFormattedNumber } from 'utils/number';

import LoadingWrapper from 'components/LoadingWrapper/LoadingWrapper';
import BRButton from 'components/BRButton/BRButton';

import { ReactComponent as Printer } from 'assets/bosta-icons/Printer.svg';

import './MobileCards.less';

const MobileCards = ({
  dataSource,
  columns,
  pagination,
  isLoading,
  allowSelection,
  isScrolling,
  handleOnClickCard,
  handlePrintSelectedRows,
  handleSelectedCards,
  mobileScreenSizes,
  filterLength = 0,
  handleClearSearch = () => {},
  scrollToFilters = () => {},
  selectedCards = [],
  setSelectedCards = () => {},
  isSelectButtonClicked = false,
  setIsSelectButtonClicked = () => false,
  intl
}) => {
  const [clickedTimes, setClickedTimes] = useState(0);

  const onCancelSelection = useCallback(() => {
    setIsSelectButtonClicked(false);
    handleSelectedCards([]);
    setSelectedCards([]);
  }, []);

  const onSelectOrderCard = ({ checked, key }) => {
    const selectedItems = checked
      ? [...selectedCards, key]
      : selectedCards.filter((cardKey) => cardKey !== key);
    setSelectedCards(selectedItems);
    handleSelectedCards(selectedItems);
  };

  const handleCheckSelection = useCallback(() => {
    setClickedTimes(clickedTimes + 1);
    setIsSelectButtonClicked(true);
    if (clickedTimes % 2 !== 0) {
      const selectedItems = dataSource.map((item) => item.key);
      setSelectedCards(selectedItems);
      handleSelectedCards(selectedItems);
    } else {
      setSelectedCards([]);
      handleSelectedCards([]);
    }
  }, [clickedTimes, isSelectButtonClicked]);

  return (
    <div className="br-mobile-cards">
      {isLoading ? (
        <LoadingWrapper
          loading={isLoading}
          className="br-mobile-cards__loading"
          indicator={
            <div>
              <LoadingOutlined spin />
              <div className="br-table__loading body">
                {intl.formatMessage({ id: 'common.loading' })}
              </div>
            </div>
          }
        />
      ) : (
        <div
          className={classnames({
            'br-table-mobile-cards-with-selection': allowSelection
          })}
        >
          {allowSelection && (
            <div
              className={classnames('br-table-mobile__fixed-header', {
                'br-table-mobile__fixed-header__with-shadow': isScrolling
              })}
            >
              <span className="br-table-mobile__fixed-header__left-section">
                <>
                  <BRButton
                    label={
                      !selectedCards.length &&
                      (isSelectButtonClicked
                        ? intl.formatMessage({ id: 'common.select_all' })
                        : intl.formatMessage({ id: 'form.select_placeholder' }))
                    }
                    className={classnames('button-md', {
                      'br-table-mobile__select-button-active':
                        isSelectButtonClicked && !selectedCards.length
                    })}
                    prefixIcon={
                      <Checkbox
                        defaultChecked
                        className={classnames({
                          'br-table-mobile__select-checkbox':
                            !isSelectButtonClicked
                        })}
                        indeterminate={
                          selectedCards.length &&
                          selectedCards.length !== dataSource?.length
                        }
                        checked={selectedCards.length === dataSource?.length}
                      />
                    }
                    onClick={handleCheckSelection}
                    type="basic"
                  />
                  {isSelectButtonClicked && (
                    <BRButton
                      label={intl.formatMessage({ id: 'common.cancel' })}
                      className="button-md"
                      type="basic"
                      onClick={onCancelSelection}
                    />
                  )}
                </>
              </span>
              {isSelectButtonClicked && (
                <BRButton
                  label={intl.formatMessage(
                    { id: 'orders_listing.actions.print' },
                    {
                      count: getFormattedNumber(selectedCards.length)
                    }
                  )}
                  className="button-md"
                  disabled={!selectedCards.length}
                  type="primary"
                  prefixIcon={<Printer />}
                  onClick={handlePrintSelectedRows}
                />
              )}
            </div>
          )}
          <List
            className="br-table-mobile-list"
            pagination={pagination}
            dataSource={dataSource}
            renderItem={(row, index) => (
              <div
                className={classnames('br-table-mobile-card', {
                  'br-table-mobile-selected-card': selectedCards.includes(
                    row?.key
                  )
                })}
              >
                {isSelectButtonClicked && (
                  <Checkbox
                    checked={selectedCards?.includes(row?.key)}
                    onChange={({ target: { checked } }) =>
                      onSelectOrderCard({ checked, key: row.key })
                    }
                  />
                )}
                <div
                  onClick={() => {
                    !isSelectButtonClicked &&
                      handleOnClickCard(dataSource[index]);
                  }}
                >
                  {columns.map((item) =>
                    item.render
                      ? item.render(row[item.dataIndex], row)
                      : row[item.dataIndex]
                  )}
                </div>
              </div>
            )}
          />
        </div>
      )}
      {mobileScreenSizes.isTinylMobileScreen &&
        isScrolling &&
        filterLength !== 0 && (
          <div className="br-mobile-cards__footer">
            <span className="br-mobile-cards__footer__label">
              {intl.formatMessage(
                { id: 'common.filters_applied' },
                { count: filterLength }
              )}
            </span>
            <span className="br-mobile-cards__footer__actions">
              <BRButton
                type="basic"
                className="button-md"
                label={intl.formatMessage({ id: 'common.adjust_filters' })}
                onClick={scrollToFilters}
              />
              <BRButton
                type="treitary"
                className="button-md"
                label={intl.formatMessage({ id: 'common.clear' })}
                onClick={handleClearSearch}
              />
            </span>
          </div>
        )}
    </div>
  );
};

export default injectIntl(MobileCards);
